import { KeyboardArrowDown } from "@mui/icons-material";
import type {} from "@mui/lab/themeAugmentation";
import {
  breadcrumbsClasses,
  buttonClasses,
  listItemIconClasses,
  listItemTextClasses,
  menuItemClasses,
  outlinedInputClasses,
  tabClasses,
  toggleButtonClasses,
} from "@mui/material";
import { createTheme, responsiveFontSizes } from "@mui/material/styles";
import { deepmerge } from "@mui/utils";
import * as tokens from "design-system/build/mui-tokens";
import getCustomerTheme from "~/customer/theme";

const base = createTheme({
  palette: {
    neutral: {
      25: tokens.primitivesColorNeutral25,
      50: tokens.primitivesColorNeutral50,
      75: tokens.primitivesColorNeutral75,
      100: tokens.primitivesColorNeutral100,
      150: tokens.primitivesColorNeutral150,
      200: tokens.primitivesColorNeutral200,
      300: tokens.primitivesColorNeutral300,
      400: tokens.primitivesColorNeutral400,
      500: tokens.primitivesColorNeutral500,
      600: tokens.primitivesColorNeutral600,
      700: tokens.primitivesColorNeutral700,
      800: tokens.primitivesColorNeutral800,
      900: tokens.primitivesColorNeutral900,
      950: tokens.primitivesColorNeutral950,
      white: tokens.primitivesColorNeutralWhite,
      black: tokens.primitivesColorNeutralBlack,
    },
    primary: {
      main: tokens.primitivesColorPurple500,
      dark: tokens.primitivesColorNeutral900,
      light: tokens.primitivesColorPurple50,
      contrastText: tokens.primitivesColorNeutralWhite,
    },
    secondary: {
      light: tokens.primitivesColorNeutral100,
      main: tokens.primitivesColorNeutral300,
    },
    text: {
      graphite: tokens.primitivesColorBrandGraphite,
      link: tokens.primitivesColorPurple500,
      visited: tokens.primitivesColorExtendedNova,
    },
    info: {
      main: tokens.primitivesColorBlue500,
    },
    header: {
      light: tokens.primitivesColorNeutral25,
    },
    common: {
      white: tokens.primitivesColorNeutralWhite,
      black: tokens.primitivesColorNeutralBlack,
      almostWhite: tokens.primitivesColorNeutral25,
      toolbarBackground: tokens.primitivesColorNeutral25,
      toolbarBorder: tokens.primitivesColorNeutral300,
      pageBackground: tokens.primitivesColorNeutralWhite,
      dimmedPageBackground: tokens.primitivesColorNeutral75,
      cardFooterText: tokens.primitivesColorNeutral600,
      cardFooterBackground: tokens.primitivesColorNeutral50,
      cardBorder: tokens.primitivesColorNeutral200,
      tableHeaderBackground: tokens.primitivesColorNeutral50,
      searchInputDarkBackground: tokens.primitivesColorNeutral800,
    },
    code: {
      main: tokens.primitivesColorNeutral700,
    },
    action: {
      disabledBackground: "",
      disabled: "",
    },
    attention: {
      main: tokens.primitivesColorRed500,
      dark: tokens.primitivesColorRed700,
    },
    status: {
      pending: tokens.primitivesColorYellow500,
      running: tokens.primitivesColorGreen500,
      stopping: tokens.primitivesColorOrange500,
      stopped: tokens.primitivesColorNeutral500,
      failed: tokens.primitivesColorRed500,
      secured: tokens.primitivesColorGreen500,
    },
    grey: {
      50: tokens.primitivesColorNeutral50,
      100: tokens.primitivesColorNeutral100,
      200: tokens.primitivesColorNeutral200,
      300: tokens.primitivesColorNeutral300,
      400: tokens.primitivesColorNeutral400,
      500: tokens.primitivesColorNeutral500,
      600: tokens.primitivesColorNeutral600,
      700: tokens.primitivesColorNeutral700,
      800: tokens.primitivesColorNeutral800,
      900: tokens.primitivesColorNeutral900,
    },
  },
});

const customerTheme = getCustomerTheme(base);

let theme = createTheme(
  deepmerge(
    {
      palette: base.palette,
      typography: {
        fontFamily: [
          "-apple-system",
          "BlinkMacSystemFont",
          '"Segoe UI"',
          "Roboto",
          '"Oxygen-Sans"',
          "Ubuntu",
          "Cantarell",
          '"Helvetica Neue"',
          "sans-serif",
        ].join(","),
        allVariants: {
          color: base.palette.primary.dark,
          overflowWrap: "anywhere",
        },
        label: {
          color: base.palette.primary.dark,
          opacity: 0.5,
          fontSize: "14px",
          display: "block",
          fontWeight: 500,
          marginBottom: "4px",
          userSelect: "none",
        },
        overline: {
          fontWeight: 400,
          fontSize: "14px",
          letterSpacing: "0.025em",
        },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            "b, strong": {
              fontWeight: 600,
            },
          },
        },
        MuiButtonBase: {
          defaultProps: {
            disableRipple: true,
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              borderRadius: "6px",
              padding: "4px",
              color: base.palette.primary.dark,
              "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.07)" },
              "&:active": {
                backgroundColor: "rgba(0, 0, 0, 0.16)",
              },
              "&:focus-visible": {
                outline: `2px solid ${tokens.primitivesColorPurple500}`,
                outlineOffset: 2,
              },
              [`&.${buttonClasses.disabled}`]: {
                opacity: 0.38,
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              height: "fit-content",
              minWidth: "fit-content",
              textTransform: "initial",
              [`&.${buttonClasses.disabled}`]: {
                opacity: 0.75,
              },
              whiteSpace: "nowrap",
              fontWeight: 500,
              borderRadius: "6px",
              "&:focus-visible": {
                outline: `2px solid ${tokens.primitivesColorPurple500}`,
                outlineOffset: 2,
              },
            },
            sizeSmall: {
              fontSize: "14px",
              lineHeight: "20px",
              padding: "8px 8px",
            },
            sizeMedium: {
              fontSize: "14px",
            },
            sizeLarge: {
              fontSize: "18px",
            },
            contained: {
              color: tokens.primitivesColorNeutralWhite,
              boxShadow: "none !important",
              transition: "initial",
              "&:hover": {
                boxShadow: "none",
              },
              [`.${buttonClasses.loadingIndicator}`]: {
                color: tokens.primitivesColorPurple500,
              },
              [`&.${buttonClasses.disabled}`]: {
                backgroundColor: tokens.primitivesColorNeutral200,
                color: tokens.primitivesColorNeutral600,
                boxShadow: "none !important",
              },
            },
            containedSecondary: {
              color: base.palette.primary.dark,
              boxShadow: "none !important",
              backgroundColor: tokens.primitivesColorNeutralWhite,
              [`&.${buttonClasses.disabled}`]: {
                backgroundColor: tokens.primitivesColorNeutral200,
                color: tokens.primitivesColorNeutral600,
                "& .MuiButton-startIcon,& .MuiButton-endIcon": {
                  color: tokens.primitivesColorNeutral500,
                },
              },
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.07)",
              },
              "&:active": {
                backgroundColor: "rgba(0, 0, 0, 0.16)",
              },
            },
            outlined: {
              [`&.${buttonClasses.disabled}`]: {
                color: tokens.primitivesColorNeutral600,
                borderColor: tokens.primitivesColorNeutral200,
                backgroundColor: tokens.primitivesColorNeutral200,
              },
            },
            outlinedPrimary: {
              color: base.palette.primary.dark,
              borderColor: "rgba(0, 0, 0, 0.12)",
              "&:hover": {
                borderColor: "transparent",
                backgroundColor: "rgba(0, 0, 0, 0.07)",
              },
              "&:active": {
                backgroundColor: "rgba(0, 0, 0, 0.16)",
              },
              [`.${buttonClasses.loadingIndicator}`]: {
                color: base.palette.primary.main,
              },
            },
            outlinedInherit: {
              borderColor: "rgba(0, 0, 0, 0.12)",
              "&:hover": {
                borderColor: "transparent",
                backgroundColor: "rgba(0, 0, 0, 0.07)",
              },
              "&:active": {
                backgroundColor: "rgba(0, 0, 0, 0.16)",
              },
              [`.${buttonClasses.loadingIndicator}`]: {
                color: "rgba(0, 0, 0, 0.87)",
              },
            },
            textPrimary: {
              color: base.palette.primary.dark,
              [`&.${buttonClasses.disabled}`]: {
                color: tokens.primitivesColorNeutral600,
              },
              "&:hover": {
                backgroundColor: tokens.primitivesColorNeutral150,
              },
              "&:active": {
                backgroundColor: tokens.primitivesColorNeutral300,
              },
            },
          },
        },
        MuiLoadingButton: {
          styleOverrides: {
            root: {
              [`&.${buttonClasses.containedSecondary}`]: {
                backgroundColor: tokens.primitivesColorNeutral200,
                "&:hover": {
                  backgroundColor: tokens.primitivesColorNeutral300,
                },
                "&:active": {
                  backgroundColor: tokens.primitivesColorNeutral400,
                },
              },
              [`&.${buttonClasses.outlinedPrimary}`]: {
                color: base.palette.primary.main,
                borderColor: base.palette.primary.main,
                "&:hover": {
                  backgroundColor: base.palette.primary.light,
                },
                "&:disabled": {
                  borderColor: tokens.primitivesColorNeutral600,
                  backgroundColor: tokens.primitivesColorNeutralWhite,
                },
                [`.${buttonClasses.loadingIndicator}`]: {
                  color: base.palette.primary.main,
                },
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            root: {
              borderRadius: "6px",
              [`&:hover:not(.${outlinedInputClasses.disabled}):not(.${outlinedInputClasses.error}):not(.${outlinedInputClasses.focused})`]:
                {
                  [` .${outlinedInputClasses.notchedOutline}`]: {
                    borderColor: base.palette.primary.dark,
                  },
                },
              [`&:hover.${outlinedInputClasses.disabled}`]: {
                [` .${outlinedInputClasses.notchedOutline}`]: {
                  borderColor: "rgba(0, 0, 0, 0.23)",
                },
              },
              [`&.${outlinedInputClasses.disabled} svg`]: {
                opacity: 0.5,
              },
            },
            input: {
              fontSize: "14px",
              "&[type=search]::-webkit-search-cancel-button": {
                cursor: "pointer",
              },
            },
            multiline: {
              padding: "8px 16px",
            },
            inputMultiline: {
              padding: 0,
              borderRadius: 0,
            },
            inputSizeSmall: {
              padding: "10px 14px",
            },
            adornedStart: {
              paddingLeft: "8px",
            },
            inputAdornedStart: {
              paddingLeft: "8px",
            },
          },
        },
        MuiPaper: {
          variants: [
            {
              props: { variant: "gradient" },
              style: {
                background: "linear-gradient(180deg, #FFFFFF 0%, #E5D4F7 100%), #FFFFFF",
              },
            },
          ],
        },
        MuiDialog: {
          defaultProps: {
            "aria-labelledby": "dialog-title",
          },
          styleOverrides: {
            paper: {
              borderRadius: "12px",
            },
          },
        },
        MuiDialogTitle: {
          defaultProps: {
            variant: "body1",
            fontWeight: 600,
            id: "dialog-title",
          },
          styleOverrides: {
            root: {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: tokens.primitivesColorNeutralBlack,
              position: "relative",
            },
          },
        },
        MuiFormHelperText: {
          defaultProps: {
            variant: "standard",
          },
        },
        MuiDialogContent: {
          styleOverrides: {
            root: {
              display: "flex",
              flexDirection: "column",
            },
          },
        },
        MuiDialogActions: {
          styleOverrides: {
            root: {
              padding: "20px 24px",
            },
          },
        },
        MuiFormLabel: {
          styleOverrides: {
            root: {
              width: "fit-content",
              fontWeight: base.typography.fontWeightMedium,
            },
          },
        },
        MuiSelect: {
          defaultProps: {
            IconComponent: KeyboardArrowDown,
            MenuProps: {
              PaperProps: {
                elevation: 1,
              },
            },
          },
          styleOverrides: {
            icon: {
              fontSize: 18,
              color: tokens.primitivesColorNeutralBlack,
            },
          },
        },
        MuiMenu: {
          defaultProps: {
            PaperProps: {
              elevation: 1,
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: "14px",
              [`&:hover, &.${menuItemClasses.root}:hover`]: {
                color: tokens.primitivesColorNeutralWhite,
                backgroundColor: base.palette.primary.main,
              },
              [`& .${listItemTextClasses.primary}, & .${listItemIconClasses.root}`]: {
                fontSize: "inherit",
                color: "inherit",
                backgroundColor: "inherit",
              },
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: "8px",
              overflow: "hidden",
              background: tokens.primitivesColorNeutralWhite,
              boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
            },
          },
        },
        MuiCardContent: {
          styleOverrides: {
            root: {
              display: "flex",
              flexDirection: "column",
            },
          },
        },
        MuiAvatar: {
          styleOverrides: {
            colorDefault: {
              fontSize: "75%",
              backgroundColor: tokens.primitivesColorPurple100,
            },
          },
        },
        MuiAvatarGroup: {
          styleOverrides: {
            avatar: {
              boxSizing: "border-box",
            },
          },
        },
        MuiSnackbarContent: {
          styleOverrides: {
            root: {
              backgroundColor: base.palette.primary.main,
            },
          },
        },
        MuiButtonGroup: {
          defaultProps: {
            disableRipple: true,
          },
        },
        MuiToggleButtonGroup: {
          styleOverrides: {
            root: {
              borderRadius: "9px",
              backgroundColor: tokens.primitivesColorNeutral100,
            },
            grouped: {
              textTransform: "none",
              fontWeight: 700,
              padding: "8px 10px",
              margin: "8px",
              border: "none",
              borderRadius: "6px",
              [`&.${toggleButtonClasses.selected}`]: {
                backgroundColor: tokens.primitivesColorNeutralWhite,
                boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
              },
              [`&.${toggleButtonClasses.selected}:hover`]: {
                backgroundColor: tokens.primitivesColorNeutralWhite,
              },
              "&:not(:first-of-type)": {
                borderRadius: "6px",
              },
              "&:first-of-type": {
                borderRadius: "6px",
              },
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            root: {
              minHeight: "unset",
              borderBottom: "1px solid rgba(0, 0, 0, 0.15)",
            },
            fixed: {
              height: "fit-content",
            },
            flexContainer: {
              gap: "16px",
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              opacity: 0.8,
              paddingBottom: "16px",
              color: base.palette.primary.dark,
              fontWeight: 500,
              fontSize: "14px",
              textTransform: "capitalize",
              minHeight: "fit-content",
              paddingLeft: 0,
              paddingRight: 0,
              minWidth: "auto",
              [`&.${tabClasses.selected}`]: {
                opacity: 1,
                color: base.palette.primary.main,
              },
            },
          },
        },
        MuiTooltip: {
          defaultProps: { variant: "dark" },
          styleOverrides: {
            tooltip: ({ ownerState }) => ({
              fontSize: "12px",
              ...(ownerState.variant === "light" && {
                color: tokens.primitivesColorNeutralBlack,
                backgroundColor: tokens.primitivesColorNeutralWhite,
                boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
              }),
              ...(ownerState.variant === "dark" && {
                color: tokens.primitivesColorNeutralWhite,
                backgroundColor: tokens.primitivesColorNeutral800,
              }),
            }),
            arrow: ({ ownerState }) => ({
              ...(ownerState.variant === "light" && {
                color: tokens.primitivesColorNeutralWhite,
                "&::before": {
                  boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                },
              }),
              ...(ownerState.variant === "dark" && {
                color: tokens.primitivesColorNeutral800,
              }),
            }),
          },
        },
        MuiPickersDay: {
          styleOverrides: {
            root: {
              fontSize: "14px",
            },
          },
        },
        MuiPickersYear: {
          styleOverrides: {
            yearButton: {
              fontSize: "14px",
            },
          },
        },
        MuiBreadcrumbs: {
          defaultProps: {
            "aria-label": "breadcrumb",
          },
          styleOverrides: {
            root: {
              [`ol > li.${breadcrumbsClasses.li}`]: {
                fontSize: "14px",
                color: base.palette.primary.dark,
                a: {
                  opacity: 0.72,
                  "&:hover": { opacity: 1 },
                },
              },
            },
            separator: {
              marginLeft: 2,
              marginRight: 2,
              opacity: 0.72,
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            outlinedError: {
              backgroundColor: tokens.primitivesColorRed50,
            },
          },
        },
        MuiDrawer: {
          defaultProps: {
            componentsProps: {
              backdrop: {
                sx: {
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                },
              },
            },
          },
        },
        MuiChip: {
          styleOverrides: {
            root: {
              "&.MuiChip-colorSuccess": {
                color: tokens.primitivesColorGreen700,
                backgroundColor: tokens.primitivesColorGreen100,
              },
            },
          },
        },
      },
    },
    customerTheme
  )
);

theme = responsiveFontSizes(theme);

if (import.meta.env.DEV) {
  /* eslint-disable @typescript-eslint/no-explicit-any */
  (window as any).theme = theme;
}

export default theme;
